import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Building2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';

interface Client {
  id: number;
  legal_name: string;
  trade_name: string;
  account_id?: number;
}

interface ClientSelectorProps {
  onClientSelect: (client: Client) => void;
  selectedClient?: Client;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({ onClientSelect, selectedClient }) => {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const clients = await apiService.getClients();
      setClients(clients);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId: string) => {
    const client = clients.find(c => c.id.toString() === clientId);
    if (client) {
      onClientSelect(client);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <AtomicLoadingScreen 
            message="Carregando clientes..." 
            className="h-16"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Building2 className="w-5 h-5 text-primary-600" />
          <span>Selecionar Cliente</span>
        </CardTitle>
        <CardDescription>
          Escolha o cliente para o qual você deseja cadastrar o feedback
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Cliente</label>
            <Select 
              value={selectedClient?.id.toString() || ''} 
              onValueChange={handleClientChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id.toString()}>
                    <div className="flex flex-col">
                      <span className="font-medium">{client.trade_name}</span>
                      <span className="text-xs text-gray-500">{client.legal_name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedClient && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">{selectedClient.trade_name}</p>
                  <p className="text-sm text-green-600">{selectedClient.legal_name}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientSelector;
